import { useEffect, useState } from "react";

import PropTypes from "prop-types";

//Material UI Components
import Box from "@mui/material/Box";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Button from "@mui/material/Button";

// import mui icons
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

import ConsumerFilterOrder from "utils/ConsumerFilterOrder";
import { getMacroCategoryOrder } from "utils/GetConsumerFilterOrder";

//redux
import { useSelector } from "react-redux";

function DriverAnalysisConsumerFilters({ handleFiltersData = () => { }, consumerFilter = {} }) {
    const productCategoryData = useSelector((state) => state.settingsStore.productCategory);

    const [open, setOpen] = useState(false);
    const [sortedMainFilters, setSortedMainFilters] = useState([]);
    const [sortedSubFilters, setSortedSubFilters] = useState([]);
    const [macroCategories, setMacroCategories] = useState([]);
    const [selectedMacroCategory, setSelectedMacroCategory] = useState("");
    const [visibleMainFilters, setVisibleMainFilters] = useState([]);

    const [selectedFilter, setSelectedFilter] = useState(null);

    const [selectedFiltersData, setSelectedFiltersData] = useState({});
    const [selectedFiltersLabelData, setSelectedFiltersLabelData] = useState([]);

    const originalOrder = [...ConsumerFilterOrder.customOrder];

    const isOralCare = process.env?.REACT_APP_ORAL_CARE?.toLowerCase() === productCategoryData?.label?.toLowerCase();
    const isSkinCleansing = process.env?.REACT_APP_SKIN_CLEANSING?.toLowerCase() === productCategoryData?.label?.toLowerCase();
    const isHairCare = process.env?.REACT_APP_HAIR_CARE?.toLowerCase() === productCategoryData?.label?.toLowerCase();
    const isSkinCare = process.env?.REACT_APP_SKIN_CARE?.toLowerCase() === productCategoryData?.label?.toLowerCase();
    const isUnileverFragranceHouse = process.env?.REACT_APP_UNILEVER_FRAGRANCE_HOUSE?.toLowerCase() === productCategoryData?.label?.toLowerCase();
    const macroOrder = getMacroCategoryOrder(productCategoryData?.label?.toLowerCase());

    let customOrder;
    if (isSkinCare) {
        customOrder = ConsumerFilterOrder.customOrderSkinCare;
    } else if (isOralCare || isSkinCleansing) {
        const sortedOriginalOrder = [...originalOrder].sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
        customOrder = sortedOriginalOrder;
    } else if (isUnileverFragranceHouse) {
        customOrder = ConsumerFilterOrder.correlationConsumerFiltersForUFH;
    } else {
        customOrder = originalOrder;
    }
    const customOrderSub = [...ConsumerFilterOrder.customOrderSub];
    const alphabeticOrderFilters = [...ConsumerFilterOrder.alphabeticOrderFilters];
    const _correlationConsumerFilters = isSkinCare ? ConsumerFilterOrder.correlationConsumerFiltersForSkinCare : (isOralCare ? ConsumerFilterOrder.correlationConsumerFiltersForOralCare : (isUnileverFragranceHouse ? ConsumerFilterOrder.correlationConsumerFiltersForUFH : ConsumerFilterOrder.correlationConsumerFilters));
    const getHairCareCustomOrder = (macroCategory) => {
        if (macroCategory === "Consumer Demographics") {
            return ConsumerFilterOrder.ConsumerDemographicsHairCare;
        }
        if (macroCategory === "Study Information") {
            return ConsumerFilterOrder.StudyInformationHairCare;
        }
        if (macroCategory === "Brand recognition") {
            return ConsumerFilterOrder.BrandRecognitionHairCare;
        }
        return [];
    };
    useEffect(() => {
        const datum = { ...selectedFiltersData };
        const labelArray = [];
        const filters = Object.keys(datum) || [];
        if (filters.length !== 0) {
            filters.forEach((bv) => {
                const selectedLabels = datum[bv];
                const displayLabel = consumerFilter["backend_variable_display_label"].filter((ele) => ele.backendVariable === bv)[0]?.displayLabel || "";

                const array = [displayLabel, selectedLabels];
                labelArray.push(array);
            });
        }
        setSelectedFiltersLabelData([...labelArray]);
    }, [selectedFiltersData, consumerFilter]);

    useEffect(() => {
        if (isHairCare) {
            const macroCatMap = consumerFilter?.["macro_category_display_label_map"] ?? {};
            const macroKeys = Object.keys(macroCatMap);
            if (macroKeys.length > 0) {
                const sortedMacroKeys = macroKeys.slice().sort((a, b) => {
                    const iA = macroOrder.indexOf(a);
                    const iB = macroOrder.indexOf(b);
                    if (iA === -1 && iB === -1) return a.localeCompare(b);
                    if (iA === -1) return 1;
                    if (iB === -1) return -1;
                    return iA - iB;
                });
                setMacroCategories(sortedMacroKeys);
                setSelectedMacroCategory(sortedMacroKeys[0]);
                const firstMacro = sortedMacroKeys[0];
                const firstMacroLabels = macroCatMap[firstMacro] || [];
                const allFilters = consumerFilter["backend_variable_display_label"] || [];
                const priorityOrder = getHairCareCustomOrder(firstMacro);
                const visible = allFilters
                    .filter((f) => firstMacroLabels.includes(f.displayLabel)) 
                    .sort((a, b) => {
                        const indexA = priorityOrder.indexOf(a.displayLabel);
                        const indexB = priorityOrder.indexOf(b.displayLabel);
                        if (indexA === -1 && indexB === -1) {
                            return a.displayLabel.localeCompare(b.displayLabel);
                        }
                        if (indexA === -1) return 1;
                        if (indexB === -1) return -1;
                        return indexA - indexB;
                    });
                setVisibleMainFilters(visible);
                setSelectedFilter(visible?.[0] ?? null);
            } else {
                // Hair Care but macro_category_display_label_map absent — fallback to all filters
                const allFilters = consumerFilter["backend_variable_display_label"] || [];
                setVisibleMainFilters(allFilters);
                setSelectedFilter(allFilters[0] ?? null);
            }
        } else if (isSkinCare) {
            // Skin Care: show filtered ordered filters
            const array = sortMainFilters(consumerFilter["backend_variable_display_label"]) || [];
            setSortedMainFilters([...array]);
            setSelectedFilter(array?.[0] || null);
        } else {
            // Oral Care (existing): sortMainFilters applies correlationConsumerFilters restriction
            const array = sortMainFilters(consumerFilter["backend_variable_display_label"]) || [];
            setSortedMainFilters([...array]);
            setSelectedFilter(array?.[0] || null);
        }
    }, [consumerFilter]);

    useEffect(() => {
        if (selectedFilter?.backendVariable) {
            const array = sortSubFilters(selectedFilter.backendVariable, selectedFilter.displayLabel) || [];
            setSortedSubFilters([...array]);
        }
    }, [selectedFilter]);

    const handleClick = () => {
        setOpen((prev) => !prev);
    };

    const handleClickAway = () => {
        console.log("clickaway");
    };

    const openButtons = () => {
        return (
            <div
                style={{
                    fontSize: "0.75rem",
                    color: "#4d4d4d",
                    width: "100%",
                    marginTop: "12px",
                }}
            >
                <button
                    onClick={handleClick}
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        outline: "none",
                        border: "1px solid rgba(0, 0, 0, 0.24)",
                        borderRadius: "5px",
                        padding: "1px 20px",
                        width: "100%",
                        background: "#fff",
                    }}
                >
                    <p
                        style={{
                            margin: "0px 0px 0px 5px",
                        }}
                    >
                        Filters
                    </p>
                    <ArrowDropDownIcon
                        sx={{
                            margin: "0px 0px 0px 5px",
                        }}
                    />
                </button>
            </div>
        );
    };

    const handleFilterLabelClick = (filter) => {
        setSelectedFilter(filter);
    };

    const handleMacroCategoryClick = (macroCategory) => {
        const macroCatMap = consumerFilter?.["macro_category_display_label_map"] ?? {};
        const labelsInMacro = macroCatMap[macroCategory] || [];
        const allFilters = consumerFilter["backend_variable_display_label"] || [];
        const priorityOrder = getHairCareCustomOrder(macroCategory);
        const visible = allFilters
            .filter((f) => labelsInMacro.includes(f.displayLabel))
            .sort((a, b) => {
                const indexA = priorityOrder.indexOf(a.displayLabel);
                const indexB = priorityOrder.indexOf(b.displayLabel);
                if (indexA === -1 && indexB === -1) {
                    return a.displayLabel.localeCompare(b.displayLabel);
                }
                if (indexA === -1) return 1;
                if (indexB === -1) return -1;
                return indexA - indexB;
            });
        setSelectedMacroCategory(macroCategory);
        const sortedVisible = [...visible];
        setVisibleMainFilters(sortedVisible);
        setSelectedFilter(sortedVisible?.[0] ?? null);
    };

    const handleOnChange = (event, selectedFilter, label, type = "individualCheck") => {
        const backendVariable = selectedFilter?.backendVariable || null;
        const datum = { ...selectedFiltersData };

        const updateIndividualCheck = () => {
            const selectedFilterForBackendVariable = datum[backendVariable] || [];
            if (event.target.checked) {
                datum[backendVariable] = [...selectedFilterForBackendVariable, label];
            } else {
                const index = selectedFilterForBackendVariable.indexOf(label);
                if (index !== -1) {
                    selectedFilterForBackendVariable.splice(index, 1);
                }
                datum[backendVariable] = [...selectedFilterForBackendVariable];
            }
            if (datum[backendVariable].length === 0) {
                delete datum[backendVariable];
            }
        };

        const updateAllCheck = () => {
            if (event.target.checked) {
                datum[backendVariable] = [...consumerFilter["backend_variable_mapping"][backendVariable]];
            } else {
                datum[backendVariable] = [];
            }
            if (datum[backendVariable].length === 0) {
                delete datum[backendVariable];
            }
        };

        if (type === "individualCheck" && backendVariable && label) {
            updateIndividualCheck();
        } else if (type === "allCheck" && backendVariable) {
            updateAllCheck();
        }

        setSelectedFiltersData({ ...datum });
    };

    const sortMainFilters = (arr) => {
        const updatedArray = arr.filter((datum) => _correlationConsumerFilters.includes(datum.displayLabel));
        const array =
            updatedArray?.sort((a, b) => {
                const indexA = customOrder.indexOf(a.displayLabel);
                const indexB = customOrder.indexOf(b.displayLabel);
                if (isOralCare) {
                    if (a.displayLabel.toLowerCase() === "sample type") return -1;
                    if (b.displayLabel.toLowerCase() === "sample type") return 1;
                }
                if (indexA === -1 && indexB === -1) {
                    return a.displayLabel.localeCompare(b.displayLabel);
                }
                if (indexA === -1) {
                    return 1;
                }
                if (indexB === -1) {
                    return -1;
                }
                return indexA - indexB;
            }) || updatedArray;

        return array;
    };

    const sortSubFilters = (backendVariable, displayLabel) => {
        const array = JSON.parse(JSON.stringify(consumerFilter?.["backend_variable_mapping"]?.[backendVariable]) || []);
        if (!alphabeticOrderFilters.includes(displayLabel)) {
            array.sort((a, b) => {
                const indexA = customOrderSub.indexOf(a);
                const indexB = customOrderSub.indexOf(b);

                // If an element is not found in customOrderSub, move it to the start (by returning -1)
                if (indexA === -1 && indexB === -1) return 0; // both are extra, maintain their order
                if (indexA === -1) return -1; // a is extra, move it to the start
                if (indexB === -1) return 1; // b is extra, move it to the start

                // Otherwise, sort based on the order in array1
                return indexA - indexB;
            });
        }
        return array;
    };

    const clearFilters = () => {
        setSelectedFiltersData([]);
        setSelectedFiltersLabelData([]);
    };

    const getCheckedStatus = () => {
        let flag;
        if (
            consumerFilter["backend_variable_mapping"]?.[selectedFilter?.backendVariable]?.length &&
            selectedFiltersData?.[selectedFilter?.backendVariable]?.length &&
            consumerFilter["backend_variable_mapping"]?.[selectedFilter?.backendVariable]?.length === selectedFiltersData?.[selectedFilter?.backendVariable]?.length
        ) {
            flag = true;
        } else {
            flag = false;
        }
        return flag;
    };

    return (
        <ClickAwayListener
            mouseEvent="onMouseDown"
            touchEvent="onTouchStart"
            onClickAway={handleClickAway}
        >
            <Box sx={{ position: "relative" }}>
                {openButtons()}
                {open && (
                    <Box
                        sx={{
                            position: "absolute",
                            top: 35,
                            left: 10,
                            border: "1px solid #dee2e6",
                            bgcolor: "background.paper",
                            width: isHairCare ? 480 : 320,
                            zIndex: 1,
                            maxHeight: isSkinCare || isHairCare ? 300 : 220,
                            minHeight: 220,
                            overflow: "hidden",
                        }}
                        className="box-shadow--2"
                    >
                        <div
                            style={{
                                display: "flex",
                                maxHeight: isSkinCare || isHairCare ? 300 : 220,
                                minHeight: 220,
                            }}
                        >
                            {/* Panel 1 — Macro Categories (Hair Care only) */}
                            {isHairCare && macroCategories.length > 0 && (
                                <div
                                    style={{
                                        width: "33%",
                                        fontSize: "12px",
                                        overflowY: "auto",
                                        backgroundColor: "#9c27b0",
                                    }}
                                >
                                    {macroCategories.map((macro, i) => (
                                        <button
                                            key={i}
                                            style={{
                                                padding: "10px 20px",
                                                display: "block",
                                                width: "100%",
                                                cursor: "pointer",
                                                fontWeight: selectedMacroCategory === macro ? 600 : 400,
                                                color: "#fff",
                                                opacity: selectedMacroCategory === macro ? 1 : 0.6,
                                                background: "none",
                                                border: "none",
                                                outline: "none",
                                                textAlign: "left",
                                            }}
                                            onClick={() => handleMacroCategoryClick(macro)}
                                        >
                                            <span style={{ fontSize: "0.75rem" }}>{macro}</span>
                                        </button>
                                    ))}
                                </div>
                            )}

                            {/* Panel 2 — Filter Labels */}
                            <div
                                style={{
                                    width: isHairCare ? "33%" : "45%",
                                    fontSize: "12px",
                                    overflowY: "auto",
                                    backgroundColor: isHairCare ? "#f8f9fa" : (isUnileverFragranceHouse? "#9c27b0" : "transparent"),
                                    borderRight: isHairCare ? "1px solid #dee2e6" : "none",
                                    padding: isHairCare ? "0px" : "6px",
                                }}
                            >
                                {(isHairCare ? visibleMainFilters : sortedMainFilters).map((ele, i) => {
                                    const key = `${ele}_${i}`;
                                    return (
                                        <button
                                            key={key}
                                            style={{
                                                padding: "10px 20px",
                                                display: "block",
                                                width: "100%",
                                                cursor: "pointer",
                                                fontWeight: selectedFilter?.backendVariable === ele?.backendVariable ? 600 : 500,
                                                color: selectedFilter?.backendVariable === ele?.backendVariable ? (isUnileverFragranceHouse? "#f8f9fa" :"#000000") : (isUnileverFragranceHouse ? "#f8f9fa" :"#777777"),
                                                background: "none",
                                                border: "none",
                                                outline: "none",
                                                textAlign: "left",
                                            }}
                                            onClick={() => handleFilterLabelClick(ele)}
                                            aria-pressed={selectedFilter?.backendVariable === ele?.backendVariable}
                                        >
                                            <span style={{ fontSize: "0.75rem" }}>{ele.displayLabel}</span>
                                        </button>
                                    );
                                })}
                            </div>

                            {/* Panel 3 — Checkboxes */}
                            <div
                                style={{
                                    width: isHairCare ? "34%" : "55%",
                                    padding: "6px 0px 0px 10px",
                                    fontSize: "12px",
                                    display: "flex",
                                    flexDirection: "column",
                                    position: "relative",
                                    background: "rgb(51, 94, 234,0.1)",
                                }}
                            >
                                <div
                                    style={{
                                        maxHeight: 250,
                                        minHeight: 250,
                                        overflowY: "auto",
                                        padding: "5px 5px 55px 5px",
                                    }}
                                >
                                    {selectedFilter && (
                                        <div
                                            style={{
                                                display: "flex",
                                                paddingBottom: "10px",
                                                borderBottom: "0.5px solid #000000",
                                            }}
                                        >
                                            <FormControlLabel
                                                key={selectedFilter?.backendVariable}
                                                label="Select All"
                                                sx={{
                                                    "& .MuiTypography-root": {
                                                        fontSize: 12,
                                                        padding: 0,
                                                        display: "block",
                                                    },
                                                }}
                                                control={
                                                    <Checkbox
                                                        sx={{
                                                            "& .MuiSvgIcon-root": {
                                                                fontSize: 18,
                                                                padding: 0,
                                                            },
                                                        }}
                                                        style={{
                                                            padding: 5,
                                                        }}
                                                        color="secondary"
                                                        checked={getCheckedStatus()}
                                                        onChange={(event) => handleOnChange(event, selectedFilter, null, "allCheck")}
                                                    />
                                                }
                                            />
                                            <Button
                                                size="small"
                                                className="custom-btn-outlined color-blue"
                                                variant="outlined"
                                                style={{
                                                    padding: "0px 0px",
                                                    textTransform: "unset",
                                                    fontSize: "10px",
                                                }}
                                                onClick={() => clearFilters()}
                                            >
                                                Clear All
                                            </Button>
                                        </div>
                                    )}
                                    {sortedSubFilters?.map((label) => {
                                        return (
                                            <div key={label}>
                                                <FormControlLabel
                                                    key={label}
                                                    sx={{
                                                        "& .MuiTypography-root": {
                                                            fontSize: 12,
                                                            margin: "5px 0px",
                                                            padding: 0,
                                                            display: "block",
                                                            paddingLeft: 1,
                                                        },
                                                    }}
                                                    style={{
                                                        margin: "0px 0px 0px 0px",
                                                        textAlign: "left",
                                                    }}
                                                    control={
                                                        <Checkbox
                                                            sx={{
                                                                "& .MuiSvgIcon-root": {
                                                                    fontSize: 18,
                                                                    padding: 0,
                                                                },
                                                            }}
                                                            style={{
                                                                padding: 5,
                                                            }}
                                                            color="secondary"
                                                            checked={selectedFiltersData && selectedFiltersData?.[selectedFilter?.backendVariable] ? selectedFiltersData?.[selectedFilter?.backendVariable].indexOf(label) !== -1 : false}
                                                            onChange={(event) => handleOnChange(event, selectedFilter, label, "individualCheck")}
                                                            value={label}
                                                        />
                                                    }
                                                    label={label.replace(/ \|/g, ",").trim()}
                                                />
                                            </div>
                                        );
                                    })}
                                </div>
                                <div
                                    style={{
                                        position: "absolute",
                                        bottom: 0,
                                        left: 0,
                                        width: "100%",
                                        display: "flex",
                                        justifyContent: "center",
                                        padding: "6px 0",
                                        background: "#eaeffd",
                                    }}
                                >
                                    <Button
                                        size="small"
                                        className="px-2 py-1"
                                        variant="outlined"
                                        style={{
                                            cursor: "pointer",
                                            width: 35,
                                            height: 25,
                                            overflow: "hidden",
                                            color: "black",
                                            background: "#fff",
                                        }}
                                        onClick={() => {
                                            handleFiltersData(selectedFiltersLabelData, selectedFiltersData);
                                            setOpen(false);
                                        }}
                                    >
                                        Apply
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </Box>
                )}
            </Box>
        </ClickAwayListener>
    );
}

DriverAnalysisConsumerFilters.propTypes = {
    handleFiltersData: PropTypes.func,
    consumerFilter: PropTypes.object,
};

export default DriverAnalysisConsumerFilters;
